import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {html} from 'lit';
import type AudienceExportSnapshot from './index.js';
import {displayApiError} from '@gl/lit/utils/notifications.js';
import {renderNotification} from '@gl/lit/utils/notifications.js';
import {AlertIntent} from '@gl/lit/components/base/alert/index.js';
import {fetcher} from '@gl/lit/utils/http.js';
import {RequestMethod} from '@gl/lit/common/constants/index.js';
import {isUndefined, isNil} from '@gl/lit/utils/index.js';

type SnapshotResponse = {
  message: string;
  last_snapshot: string;
};

export type Model = {
  audienceExportId: number;
  lastSnapshot?: Date;
  enabled: boolean;
  active: boolean;
  timeoutId?: number;
};

export const Msg = makeVariants({
  submit: Variant<void>()('submit'),
  updateEnabled: Variant<boolean>()('update-enabled'),
  updateTimeout: Variant<number>()('update-timeout'),
  resetLastSnapshot: Variant<string>()('reset-snapshot'),
});

export type Msg = VariantsFor<typeof Msg>;

const MINUTES_BETWEEN_ATTEMPTS = 5;

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const active = <boolean>props.get('activeAudience');
  const lastSnapshotText = <string>props.get('lastSnapshot');
  const lastSnapshot = isUndefined(lastSnapshotText)
    ? undefined
    : <Date>new Date(lastSnapshotText);

  return [
    {
      audienceExportId: <number>props.get('audienceExportId'),
      lastSnapshot: lastSnapshot,
      enabled: calculateEnabled(lastSnapshot),
      timeoutId: undefined,
      active,
    },
    cmd(scheduleNextUpdate),
  ] as const;
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'submit': {
      return [
        {
          ...model,
        },
        cmd(submit),
      ] as const;
    }

    case 'update-enabled': {
      return [
        {
          ...model,
          enabled: msg.value,
        },
        cmd(scheduleNextUpdate),
      ] as const;
    }

    case 'reset-snapshot': {
      return {
        ...model,
        lastSnapshot: isUndefined(msg.value) ? undefined : new Date(msg.value),
      } as const;
    }

    case 'update-timeout': {
      return {
        ...model,
        timeoutId: msg.value,
      } as const;
    }
  }
}

async function submit(host: AudienceExportSnapshot) {
  try {
    const url = `/api/v2/audience_exports/${host.model.audienceExportId}/snapshot`;

    const response = await fetcher<SnapshotResponse>(url, {
      method: RequestMethod.POST,
    });

    renderNotification(response['message'], AlertIntent.PRIMARY);
    host.handleUpdate(Msg.resetLastSnapshot(response['last_snapshot']));
    updateEnabled(host);
  } catch (err) {
    displayApiError(err);
    updateEnabled(host);
  }
}

function calculateEnabled(lastSnapshot: Readonly<Date | undefined>) {
  const minutes = minutesSinceLastSnapshot(lastSnapshot);
  if (isNil(minutes)) return true;
  return minutes >= MINUTES_BETWEEN_ATTEMPTS;
}

function minutesUntilEnabled(lastSnapshot: Readonly<Date | undefined>) {
  const minutes = minutesSinceLastSnapshot(lastSnapshot);
  if (isUndefined(minutes)) return 0;
  if (minutes >= MINUTES_BETWEEN_ATTEMPTS) return 0;
  return MINUTES_BETWEEN_ATTEMPTS - minutes;
}

function minutesSinceLastSnapshot(lastSnapshot: Readonly<Date | undefined>) {
  if (isNil(lastSnapshot)) return undefined;

  const diff = new Date().getTime() - lastSnapshot.getTime();
  return Math.floor(diff / 1000 / 60);
}

function updateEnabled(host: AudienceExportSnapshot) {
  if (isUndefined(host)) return;

  const enabled = calculateEnabled(host.model.lastSnapshot);
  host.handleUpdate(Msg.updateEnabled(enabled));
}

function scheduleNextUpdate(host: AudienceExportSnapshot) {
  if (isUndefined(host)) return;

  cancelTimeout(host.model);
  host.handleUpdate(
    Msg.updateTimeout(Number(setTimeout(updateEnabled, 3000, host)))
  );
}

function cancelTimeout(model: Readonly<Model>) {
  if (model.timeoutId) {
    clearTimeout(model.timeoutId);
  }
}

// -- View

function view(this: AudienceExportSnapshot, model: Readonly<Model>) {
  if (!model.audienceExportId) return html``;

  return html`
    <fly-tooltip class="audience-export-snapshot__tooltip">
      <span class="audience-export-snapshot__tooltip--content" slot="content">
        ${model.active && model.enabled
          ? 'Trigger an export. Can take up to 5 minutes to start.'
          : model.active
            ? `Please wait ${minutesUntilEnabled(
                model.lastSnapshot
              )} minutes before attempting to trigger a new export.`
            : 'This export is not active.'}
      </span>
      <fly-list-item
        ?disabled=${!model.active}
        hasicon
        preventSelection
        @click=${() => {
          if (!model.active) return;
          this.handleUpdate(Msg.submit());
        }}
      >
        Run
        <div slot="icon">
          <fly-icon icon="play_circle"></fly-icon>
        </div>
      </fly-list-item>
    </fly-tooltip>
  `;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
